/**
 * School Mobile App Drafts API Contract (release lines U8, R10, R13, KTD4)
 *
 * GET  /v1/schools/:school_id/mobile-app/drafts
 * PUT  /v1/schools/:school_id/mobile-app/drafts/app-name
 * POST /v1/schools/:school_id/mobile-app/drafts/:slotName/upload-signature
 * PUT  /v1/schools/:school_id/mobile-app/drafts/:slotName
 *
 * The school's drafts of the fields baked into its app's binary: the app name,
 * the icon, the splash image and the Android adaptive icon. A save stores the
 * value in `MobileAppFieldDraft` and nothing else. The live name and the live
 * build slots are what the build snapshot and the public asset manifest read;
 * they change only when staff release the draft.
 *
 * `:slotName` is one of `SCHOOL_DRAFT_ASSET_SLOTS`. The live slot routes
 * (`/mobile-app-assets/*`) keep refusing these names.
 *
 * AN IMAGE IS VERIFIED AT CLOUDINARY BEFORE ANYTHING IS WRITTEN, as on the live
 * pre-login lane: the public id must sit under this app's own folder for the
 * slot, and the dimensions, mime type, size and version come from Cloudinary.
 * The request body's metadata is discarded.
 */

import { z } from 'zod'
import { SchoolAssetUploadSignatureResponseSchema } from '../../schemas/entities/mobile-app-asset'
import {
  AppNameDraftValueSchema,
  BUILD_DRAFT_FIELDS,
  ImageDraftValueSchema
} from '../../schemas/entities/school-app-field-draft'

/** One build draft as the editor reads it. The image is a delivery URL, never the stored commit. */
export const SchoolAppDraftSchema = z.object({
  fieldKey: z.enum(BUILD_DRAFT_FIELDS),
  value: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), name: z.string() }),
    z.object({
      kind: z.literal('image'),
      url: z.string().url(),
      width: z.number().int().nullable(),
      height: z.number().int().nullable()
    })
  ]),
  savedAt: z.string().datetime(),
  releaseRequestedAt: z.string().datetime().nullable(),
  /** Set once staff copied the draft into the app; a later save clears it. */
  appliedAt: z.string().datetime().nullable()
})
export type SchoolAppDraft = z.infer<typeof SchoolAppDraftSchema>

/** GET response: every build draft the app has, applied or not. */
export const GetSchoolAppDraftsResponseSchema = z.object({ drafts: z.array(SchoolAppDraftSchema) })
export type GetSchoolAppDraftsResponse = z.infer<typeof GetSchoolAppDraftsResponseSchema>

/** PUT …/drafts/app-name body. The 30-character cap is the stores' (R19). */
export const SaveSchoolAppNameDraftRequestSchema = AppNameDraftValueSchema
export type SaveSchoolAppNameDraftRequest = z.infer<typeof SaveSchoolAppNameDraftRequestSchema>

/**
 * PUT …/drafts/:slotName body: the upload's coordinates, the slot commit shape.
 * Only `cloudinaryPublicId` is used; the rest is re-read from Cloudinary.
 */
export const SaveSchoolImageDraftRequestSchema = ImageDraftValueSchema
export type SaveSchoolImageDraftRequest = z.infer<typeof SaveSchoolImageDraftRequestSchema>

/** Response of both saves: the draft as it now stands. */
export const SaveSchoolAppDraftResponseSchema = SchoolAppDraftSchema
export type SaveSchoolAppDraftResponse = z.infer<typeof SaveSchoolAppDraftResponseSchema>

/**
 * POST …/drafts/:slotName/upload-signature response: the signed-widget
 * parameters, into the slot's DRAFT folder, with the school guard.
 */
export const SchoolAppDraftUploadSignatureResponseSchema = SchoolAssetUploadSignatureResponseSchema
export type SchoolAppDraftUploadSignatureResponse = z.infer<typeof SchoolAppDraftUploadSignatureResponseSchema>

/**
 * Failure vocabulary. `SLOT_NOT_DRAFTABLE` is a slot outside
 * `SCHOOL_DRAFT_ASSET_SLOTS`; `ASSET_NOT_IN_SCOPE` an upload outside this app's
 * folder for the slot (another app's, or another slot's); `VALIDATION_FAILED`
 * a value the field's own rule refuses (a name over 30 characters, an icon of
 * the wrong size).
 */
export const SCHOOL_APP_DRAFT_ERROR_CODES = [
  'INVALID_PARAMS',
  'NOT_AUTHORIZED',
  'NO_MOBILE_APP',
  'SLOT_NOT_DRAFTABLE',
  'ASSET_NOT_IN_SCOPE',
  'ASSET_NOT_FOUND',
  'RATE_LIMITED',
  'CONFIGURATION_ERROR',
  'VALIDATION_FAILED',
  'INTERNAL_ERROR'
] as const
export type SchoolAppDraftErrorCode = (typeof SCHOOL_APP_DRAFT_ERROR_CODES)[number]

export {
  SCHOOL_DRAFT_ASSET_SLOTS,
  isSchoolDraftAssetSlotName,
  type SchoolDraftAssetSlotName
} from '../../schemas/entities/mobile-app-asset'
export { SCHOOL_APP_NAME_MAX_LENGTH } from '../../schemas/entities/school-app-field-draft'
